import { memo, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, LabelList } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartConfig,
} from '@parrot-software/design-system';
import type { TransformedChartData } from '@reports/presentation/utils/chartDataTransformers';
import { formatAxisValue } from '@reports/presentation/utils/dataFormatters';
import { truncateLabelByWidth } from '@reports/presentation/utils/chartLabelRenderers';
import { ReportDataFormats } from '@reports/domain/types';
import type { ReportDataConfig } from '@reports/domain/types';

export interface ReportHorizontalBarChartProps {
  chartData: TransformedChartData[];
  barColor: string;
  showTooltip: boolean;
  showTopBarLabels: boolean;
  showLabels: boolean;
  showLegend: boolean;
  tooltipFormatter: (value: any, name: string | number, item: any) => React.ReactElement;
  topLabelFormatter: (value: number) => string;
  chartConfig: ChartConfig;
  format?: ReportDataConfig;
}

/**
 * ReportHorizontalBarChart - Horizontal bar chart renderer for reports
 *
 * Renders a horizontal bar chart using Recharts with optional tooltips and top labels.
 * Automatically adds currency symbol ($) to XAxis when format type is CURRENCY.
 * Sorts data in descending order (highest values at the top).
 * Truncates labels to prevent overflow within bars.
 * Memoized to prevent unnecessary re-renders.
 *
 * @param {ReportHorizontalBarChartProps} props - Component properties
 * @param {TransformedChartData[]} props.chartData - Chart data in Recharts format
 * @param {string} props.barColor - Bar fill color
 * @param {boolean} props.showTooltip - Whether to show tooltips on hover
 * @param {boolean} props.showTopBarLabels - Whether to show labels on top of bars
 * @param {boolean} props.showLabels - Whether to show labels inside bars
 * @param {boolean} props.showLegend - Whether to show chart legend
 * @param {Function} props.tooltipFormatter - Function to format tooltip content
 * @param {Function} props.topLabelFormatter - Function to format top labels
 * @param {ChartConfig} props.chartConfig - Chart configuration
 * @param {ReportDataConfig} [props.format] - Data format configuration for axis formatting
 * @returns {React.ReactElement} Horizontal bar chart component
 *
 * @example
 * <ReportHorizontalBarChart
 *   chartData={transformedData}
 *   barColor="#EB6668"
 *   showTooltip={true}
 *   showTopBarLabels={true}
 *   showLabels={true}
 *   showLegend={true}
 *   tooltipFormatter={barTooltipFormatter}
 *   topLabelFormatter={topLabelFormatter}
 *   chartConfig={{ value: { label: "Total Sales MXN", color: "#000000" } }}
 *   format={{ type: ReportDataFormats.CURRENCY }}
 * />
 */
const ReportHorizontalBarChartComponent = ({
  chartData,
  barColor,
  showTooltip,
  showTopBarLabels,
  showLabels,
  showLegend,
  tooltipFormatter,
  topLabelFormatter,
  chartConfig,
  format,
}: ReportHorizontalBarChartProps) => {
  // Sort data in descending order (highest values at the top)
  const sortedChartData = useMemo(() => {
    return [...chartData].sort((a, b) => b.value - a.value);
  }, [chartData]);

  // Calculate max value for XAxis domain
  const maxValue = useMemo(() => {
    if (sortedChartData.length === 0) return 100;
    const max = Math.max(...sortedChartData.map((item) => item.value));

    // Add 10% padding to the max value for better visualization
    return Math.ceil(max * 1.1);
  }, [sortedChartData]);

  // Create XAxis formatter that adds "$" prefix for currency format
  const xAxisFormatter = useMemo(() => {
    const isCurrency = format?.type === ReportDataFormats.CURRENCY;

    return (value: number): string => {
      const formattedValue = formatAxisValue(value);
      return isCurrency && formattedValue ? `$${formattedValue}` : formattedValue;
    };
  }, [format]);

  return (
    <ChartContainer className="h-[275px] w-full" config={chartConfig}>
      <ResponsiveContainer height="100%" width="100%">
        <BarChart data={sortedChartData} layout="vertical" margin={{ top: 0, left: 4, right: 60 }}>
          <CartesianGrid horizontal={false} />
          <YAxis axisLine={false} dataKey="name" hide tickLine={false} type="category" width={0} />
          <XAxis
            axisLine={false}
            domain={[0, maxValue]}
            tickFormatter={xAxisFormatter}
            tickLine={false}
            type="number"
          />

          {showTooltip && (
            <ChartTooltip
              content={
                <ChartTooltipContent formatter={tooltipFormatter} labelClassName="font-normal text-muted-foreground" />
              }
            />
          )}
          <Bar dataKey="value" fill={barColor} maxBarSize={32} radius={[0, 2, 2, 0]}>
            {showLabels && (
              <LabelList
                content={({ x, y, height, value, width }) => (
                  <text
                    className="text-xs font-medium fill-primary-foreground"
                    dominantBaseline="middle"
                    textAnchor="start"
                    x={Number(x) + 8}
                    y={Number(y) + Number(height) / 2}
                  >
                    {truncateLabelByWidth(String(value), Number(width))}
                  </text>
                )}
                dataKey="name"
                offset={8}
                position="insideLeft"
              />
            )}

            {showTopBarLabels && (
              <LabelList
                className="text-xs fill-foreground"
                dataKey="value"
                formatter={topLabelFormatter}
                offset={8}
                position="right"
              />
            )}
          </Bar>
          {showLegend && <ChartLegend content={<ChartLegendContent nameKey="value" />} verticalAlign="bottom" />}
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

/**
 * Memoized version of ReportHorizontalBarChart
 *
 * Prevents re-renders when props haven't changed, improving performance.
 */
export const ReportHorizontalBarChart = memo(ReportHorizontalBarChartComponent);
